import { ChevronDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Partner } from '@/types/partner';
import { cn } from '@/lib/utils';

type LinkedBrandsFieldProps = {
  partners: Partner[];
  selectedIds: string[];
  allSelected: boolean;
  multiple: boolean;
  allowSelectAll?: boolean;
  error?: string;
  onToggle: (_partnerId: string) => void;
  onToggleAll: () => void;
};

const formatSelectedLabel = (partners: Partner[], selectedIds: string[]) => {
  const selected = partners.filter((partner) => selectedIds.includes(partner.id));
  if (selected.length === 0) return '';
  if (selected.length === 1) return selected[0].name;
  if (selected.length === 2) return `${selected[0].name}, ${selected[1].name}`;
  return `${selected.slice(0, -1).map((partner) => partner.name).join(', ')} e ${selected[selected.length - 1].name}`;
};

export const LinkedBrandsField = ({
  partners,
  selectedIds,
  allSelected,
  multiple,
  allowSelectAll = true,
  error,
  onToggle,
  onToggleAll,
}: LinkedBrandsFieldProps) => {
  const label =
    allSelected && multiple ? 'Todas as marcas' : formatSelectedLabel(partners, selectedIds);

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-gray-900">
        {multiple ? 'Marcas' : 'Marca'} <span className="text-red-500">*</span>
      </label>
      <Popover modal={false}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 text-sm shadow-xs shadow-input',
              error ? 'border-red-500' : 'border-input',
              label ? 'text-gray-900' : 'text-muted-foreground'
            )}
          >
            <span className="truncate">{label || 'Selecione'}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
          className="z-[80] max-h-64 w-[var(--radix-popover-trigger-width)] overflow-y-scroll overscroll-contain p-2"
        >
          {multiple && allowSelectAll ? (
            <label className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-gray-50">
              <Checkbox checked={allSelected} onCheckedChange={() => onToggleAll()} />
              <span>Todas as marcas</span>
            </label>
          ) : null}
          {partners.map((partner) => (
            <label
              key={partner.id}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-gray-50"
            >
              {multiple ? (
                <Checkbox
                  checked={selectedIds.includes(partner.id)}
                  onCheckedChange={() => onToggle(partner.id)}
                />
              ) : (
                <input
                  type="radio"
                  className="h-4 w-4"
                  name="new-user-brand"
                  checked={selectedIds[0] === partner.id}
                  onChange={() => onToggle(partner.id)}
                />
              )}
              <span className="truncate">{partner.name}</span>
            </label>
          ))}
        </PopoverContent>
      </Popover>
      {error ? <p className="text-xs font-medium text-red-500">{error}</p> : null}
    </div>
  );
};
